import { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: ReactNode;
  /** Beholdt for bakoverkompatibilitet — ignoreres (alltid sentrert modal nå). */
  blur?: boolean;
}

/**
 * Tidligere en iOS bunn-sheet. Nå en ren, sentrert modal (portal til <body>)
 * for å matche resten av appen og unngå glitch fra transformerte foreldre.
 * Props-API beholdt så alle kallsteder fungerer uendret.
 */
export default function Sheet({ open, onClose, title, subtitle, children }: SheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] animate-backdrop-in" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg bg-card rounded-[16px] shadow-pop border border-line animate-fade-up max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-3 sticky top-0 bg-card z-10">
          <div className="min-w-0">
            {title && <h2 className="text-section text-night">{title}</h2>}
            {subtitle && <p className="text-[14px] text-muted mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 -mr-1 -mt-0.5 rounded-[9px] text-faint hover:text-night hover:bg-black/[0.05] transition-colors shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 pb-6">{children}</div>
      </div>
    </div>,
    document.body
  );
}
